import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { MyErrorStateMatcher } from '@app-core/models/core.model';
import { DataService } from '@app-core/services/data/data.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
})
export class InputComponent implements OnInit, OnDestroy {
  @Input() public form: FormGroup;
  @Input() public config: any = {};
  @Input() inputCurrentMetricUnit: string;

  public errorStateMatcher = new MyErrorStateMatcher();
  public currentMetricUnit: string;
  private previousUnit: string | null = null;
  private unitSub: Subscription;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public resolvedMin: number;
  public resolvedMax: number;

  constructor(private dataService: DataService) {}

  ngOnInit(): void {
    this.unitSub = this.dataService._currentMetricUnit.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (!value || value === this.previousUnit) return;

      // 🔹 Apply baseline “no conversion” ONLY for:
      //   - asset.speedUpperLimit  (existing fix)
      //   - GPIO max/min (skipInitialUnitConversion: true)
      const shouldSkipBaseline = !this.previousUnit && this.config?.skipInitialUnitConversion;

      if (shouldSkipBaseline) {
        this.previousUnit = value;
        this.currentMetricUnit = value;
        this.updateMinMaxValidators();
        this.updateResolvedBounds();
        return; // ⬅️ do NOT touch control value on first emission
      }

      const control = this.form.get(this.config?.key);
      if (control && this.config?.isUnitConversionRequired) {
        const oldDisplayValue = control.value;
        const valueInMilesBefore = this.convertToMiles(oldDisplayValue, this.previousUnit);
        const newDisplayValue = this.convertToDisplayValue(valueInMilesBefore, value);
        const valueInMilesAfter = this.convertToMiles(newDisplayValue, value);

        const unitSwitchOnly = Math.abs(valueInMilesBefore - valueInMilesAfter) <= 0.001;

        if (unitSwitchOnly) {
          if (+oldDisplayValue !== +newDisplayValue) {
            control.setValue(newDisplayValue, { emitEvent: false });
          }
          control.markAsPristine();
          if (this.form.dirty) this.form.markAsPristine();
        } else {
          if (+oldDisplayValue !== +newDisplayValue) {
            control.setValue(newDisplayValue);
          }
        }
      }

      this.previousUnit = value;
      this.currentMetricUnit = value;
      this.updateMinMaxValidators();
      this.updateResolvedBounds();
    });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.unitSub?.unsubscribe();
  }

  getResolvedPlaceholder(): string {
    return typeof this.config?.placeholder === 'function' ? this.config.placeholder(this.currentMetricUnit) : this.config?.placeholder;
  }

  private updateMinMaxValidators(): void {
    const control = this.form.get(this.config?.key);
    if (!control) return;

    const min = typeof this.config.min === 'function' ? this.config.min(this.currentMetricUnit) : this.config.min;
    const max = typeof this.config.max === 'function' ? this.config.max(this.currentMetricUnit) : this.config.max;

    const validators = [];
    if (this.config.required) validators.push(Validators.required);
    if (min !== undefined) validators.push(Validators.min(min));
    if (max !== undefined) validators.push(Validators.max(max));

    control.setValidators(validators);
    control.updateValueAndValidity();
  }

  private roundToDecimals(n: any, dp = 2): number {
    const x = Number(n);
    if (!isFinite(x)) return x as any;
    const f = Math.pow(10, dp);
    return Math.round((x + Number.EPSILON) * f) / f;
  }

  getResolvedMin(): number {
    const min = this.config?.min;
    const value = typeof min === 'function' ? min(this.currentMetricUnit) : min;
    return this.roundToDecimals(value, 2);
  }

  getResolvedMax(): number {
    const max = this.config?.max;
    const value = typeof max === 'function' ? max(this.currentMetricUnit) : max;
    return this.roundToDecimals(value, 2);
  }

  private toNum(v: any): number | null {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  /** Always resolve factor as KM per MI */
  private getKmPerMi(): number {
    const raw = this.toNum(this.config?.conversionFormula);
    if (raw == null || raw <= 0) return 1.609344;

    // If config gives miles-per-km (~0.62), invert
    if (raw > 0 && raw < 1) return 1 / raw;

    // If config gives km-per-mi (~1.6), use directly
    return raw;
  }

  /** Converts miles → display unit */
  private convertToDisplayValue(valueInMiles: any, targetUnit: string): number {
    if (!this.config?.isUnitConversionRequired) return Number(valueInMiles) || 0;

    const miles = this.toNum(valueInMiles);
    if (miles == null) return 0;

    const factor = this.getKmPerMi();
    let out = targetUnit === 'Kilometers' ? miles * factor : miles;

    // round to 2dp
    out = Math.round(out * 100) / 100;

    //tolerance snapping: handle values like 96.9999 → 97
    const nearest = Math.round(out);
    if (Math.abs(out - nearest) < 0.05) {
      out = nearest;
    }

    // also snap to .5 steps if needed (e.g. 97.499 → 97.50)
    const nearestHalf = Math.round(out * 2) / 2;
    if (Math.abs(out - nearestHalf) < 0.02) {
      out = nearestHalf;
    }

    return +out.toFixed(2);
  }

  /** Converts display unit → miles (high precision, no snapping) */
  private convertToMiles(displayValue: any, sourceUnit: string): number {
    if (!this.config?.isUnitConversionRequired) return Number(displayValue) || 0;

    const disp = this.toNum(displayValue);
    if (disp == null) return 0;

    const factor = this.getKmPerMi();
    const out = sourceUnit === 'Kilometers' ? disp / factor : disp;

    // keep precision (4dp), don’t round too aggressively
    return Math.round(out * 10000) / 10000;
  }

  /**
   * Prevent inputting characters other than numbers and one dot
   */
  public onKeyPress(event: KeyboardEvent) {
    if (this.config?.type === 'number') {
      const isFloat = this.config.allowFloat;
      const isNumber = /[0-9]/.test(event.key);
      const isDot = event.key === '.';
      const isBackspace = event.key === 'Backspace';

      if (!isNumber && !(isFloat && isDot) && !isBackspace) {
        event.preventDefault();
      }
    }
  }

  public clearValue() {
    this.form.get(this.config.key)?.patchValue('');
  }

  private updateResolvedBounds(): void {
    const min = typeof this.config?.min === 'function' ? this.config.min(this.currentMetricUnit) : this.config?.min;
    const max = typeof this.config?.max === 'function' ? this.config.max(this.currentMetricUnit) : this.config?.max;

    this.resolvedMin = this.roundToDecimals(min, 2);
    this.resolvedMax = this.roundToDecimals(max, 2);
  }
}
